import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Image as ImageIcon, Heart, MessageSquare, Eye, Upload, 
  Filter, Sparkles, X, ChevronLeft, ChevronRight, Share2, 
  Maximize2, Send, Tag, Flame, CheckCircle2, User
} from "lucide-react";
import FaIcon from "../components/FaIcon";

export default function Gallery() {
  const { user } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("recent");
  
  // Lightbox
  const [activeImage, setActiveImage] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  // Upload Modal
  const [uploadModal, setUploadModal] = useState(false);
  const [newImage, setNewImage] = useState({
    title: "",
    description: "",
    image_url: "",
    category: "screenshots",
    tags: ""
  });

  const loadImages = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/gallery/images?category=${category}&sort=${sort}`);
      setImages(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
  }, [category, sort]);

  const handleLike = async (imageId, e) => {
    if (e) e.stopPropagation();
    if (!user) {
      toast.error("Trebuie să fii autentificat pentru a aprecia o imagine!");
      return;
    }
    try {
      const res = await api.post(`/gallery/images/${imageId}/like`);
      toast.success(res.data.is_liked ? "Ai apreciat imaginea!" : "Apreciere eliminată");
      
      // Update locally
      setImages(prev => prev.map(img => {
        if (img.id === imageId) {
          const likes = img.likes || [];
          const newLikes = res.data.is_liked ? [...likes, user.id] : likes.filter(id => id !== user.id);
          return { ...img, likes: newLikes, likes_count: newLikes.length };
        }
        return img;
      }));

      if (activeImage && activeImage.id === imageId) {
        const likes = activeImage.likes || [];
        const newLikes = res.data.is_liked ? [...likes, user.id] : likes.filter(id => id !== user.id);
        setActiveImage({ ...activeImage, likes: newLikes, likes_count: newLikes.length });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handlePostComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Trebuie să fii autentificat!");
      return;
    }
    if (!commentText.trim()) return;

    try {
      setSubmittingComment(true);
      const res = await api.post(`/gallery/images/${activeImage.id}/comments`, { content: commentText });
      toast.success("Comentariu adăugat!");
      
      const newComment = res.data.comment;
      const updatedComments = [...(activeImage.comments || []), newComment];
      
      setActiveImage(prev => ({ ...prev, comments: updatedComments, comments_count: updatedComments.length }));
      setImages(prev => prev.map(img => img.id === activeImage.id ? { ...img, comments: updatedComments, comments_count: updatedComments.length } : img));
      setCommentText("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!newImage.title.trim() || !newImage.image_url.trim()) {
      toast.error("Completați titlul și linkul imaginii!");
      return;
    }
    try {
      const payload = {
        ...newImage,
        tags: newImage.tags.split(",").map(t => t.trim().toLowerCase()).filter(Boolean)
      };
      await api.post("/gallery/images", payload);
      toast.success("Imagine încărcată cu succes în Galerie!");
      setUploadModal(false);
      setNewImage({ title: "", description: "", image_url: "", category: "screenshots", tags: "" });
      loadImages();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8" data-testid="media-gallery-page">
      {/* Header Banner */}
      <div className="relative overflow-hidden bg-[#070a10] border border-[#141a29] p-6 sm:p-8 rounded-xl shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[var(--tf-primary)]/10 blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-[10px] font-mono uppercase font-bold bg-[var(--tf-primary)]/20 text-[var(--tf-primary)] border border-[var(--tf-primary)]/40 rounded">
                COMMUNITY MEDIA HUB
              </span>
              <span className="text-[10px] font-mono text-zinc-500">• 4K HDR SUPPORT</span>
            </div>
            <h1 className="text-2xl sm:text-4xl font-black text-white font-chakra uppercase tracking-tight">
              GALERIE MEDIA & ALBUME INDUNGI
            </h1>
            <p className="text-xs sm:text-sm text-zinc-400 font-mono max-w-2xl">
              Momente memorabile, capturi de ecran din Counter-Strike 1.6 și CS2, setup-uri de gaming și artă dedicată comunității.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {user && (
              <button
                onClick={() => setUploadModal(true)}
                className="tf-btn-primary px-5 py-2.5 text-xs font-chakra font-black uppercase tracking-wider flex items-center gap-2 shadow-[0_0_20px_rgba(255,79,0,0.3)] rounded"
              >
                <Upload className="w-4 h-4" />
                <span>Încarcă Imagine</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-[#080c14] border border-[#141a29] p-4 rounded-xl shadow-md">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono text-zinc-400 font-bold uppercase flex items-center gap-1.5 mr-2">
            <Filter className="w-3.5 h-3.5 text-[var(--tf-primary)]" />
            <span>Album:</span>
          </span>
          {[
            { id: "all", label: "Toate" },
            { id: "screenshots", label: "Screenshots CS" },
            { id: "esports", label: "Highlights & Meciuri" },
            { id: "setups", label: "Setup Gaming" },
            { id: "wallpapers", label: "Wallpapers & Art" }
          ].map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={`px-3 py-1.5 text-xs font-chakra font-bold uppercase tracking-wider transition-all rounded ${
                category === cat.id
                  ? "bg-[var(--tf-primary)] text-black shadow-md font-black"
                  : "bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10 border border-white/5"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-zinc-400 font-bold uppercase">Sortare:</span>
          <button
            onClick={() => setSort("recent")}
            className={`px-3 py-1 text-xs font-mono font-bold uppercase rounded ${
              sort === "recent" ? "bg-white/15 text-white border border-white/20" : "bg-white/5 text-zinc-500 hover:text-zinc-300"
            }`}
          >
            Recente
          </button>
          <button
            onClick={() => setSort("popular")}
            className={`px-3 py-1 text-xs font-mono font-bold uppercase rounded flex items-center gap-1 ${
              sort === "popular" ? "bg-amber-500/20 text-amber-300 border border-amber-500/40" : "bg-white/5 text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <Flame className="w-3 h-3 text-amber-400" />
            <span>Populare</span>
          </button>
        </div>
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="p-16 text-center text-xs font-mono text-zinc-500">Se încarcă galeria media...</div>
      ) : images.length === 0 ? (
        <div className="p-16 text-center bg-[#080c14] border border-[#141a29] rounded-xl space-y-3">
          <ImageIcon className="w-12 h-12 text-zinc-600 mx-auto opacity-50" />
          <h3 className="text-sm font-chakra font-bold text-white uppercase">Nu există imagini în această categorie</h3>
          <p className="text-xs font-mono text-zinc-500">Fii primul care încarcă o captură de ecran sau un setup!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((img) => {
            const isLiked = img.likes?.includes(user?.id);

            return (
              <div
                key={img.id}
                onClick={() => setActiveImage(img)}
                className="group relative bg-[#080c14] border border-[#161f30] hover:border-[var(--tf-primary)]/50 rounded-xl overflow-hidden shadow-xl cursor-pointer transition-all duration-300 flex flex-col justify-between"
              >
                {/* Media Image Container */}
                <div className="relative aspect-video w-full overflow-hidden bg-black/60">
                  <img
                    src={img.image_url}
                    alt={img.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                    <span className="text-xs font-chakra font-bold text-white uppercase flex items-center gap-1.5">
                      <Maximize2 className="w-3.5 h-3.5 text-[var(--tf-primary)]" />
                      <span>Deschide Lightbox</span>
                    </span>
                  </div>

                  <div className="absolute top-2.5 left-2.5">
                    <span className="px-2 py-0.5 text-[9px] font-mono uppercase font-bold bg-black/75 backdrop-blur-md text-amber-300 border border-amber-500/40 rounded">
                      {img.category}
                    </span>
                  </div>
                </div>

                {/* Info Card */}
                <div className="p-4 space-y-3">
                  <div>
                    <h3 className="text-sm font-chakra font-black uppercase text-white tracking-wide group-hover:text-[var(--tf-primary)] transition-colors line-clamp-1">
                      {img.title}
                    </h3>
                    <div className="flex items-center gap-2 text-[11px] font-mono text-zinc-400 mt-1">
                      <span>De</span>
                      <strong className="text-zinc-200">{img.author_username}</strong>
                    </div>
                  </div>

                  {/* Actions & Counters */}
                  <div className="pt-3 border-t border-white/5 flex items-center justify-between text-xs font-mono text-zinc-400">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1" title="Vizualizări">
                        <Eye className="w-3.5 h-3.5 text-zinc-500" />
                        <span>{img.views || 0}</span>
                      </span>
                      <span className="flex items-center gap-1" title="Comentarii">
                        <MessageSquare className="w-3.5 h-3.5 text-zinc-500" />
                        <span>{img.comments_count || img.comments?.length || 0}</span>
                      </span>
                    </div>

                    <button
                      onClick={(e) => handleLike(img.id, e)}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                        isLiked ? "bg-rose-500/20 text-rose-400 border border-rose-500/40" : "hover:bg-white/5 text-zinc-400 hover:text-white"
                      }`}
                    >
                      <Heart className={`w-3.5 h-3.5 ${isLiked ? "fill-rose-500 text-rose-500" : ""}`} />
                      <span className="font-bold">{img.likes_count || img.likes?.length || 0}</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* LIGHTBOX MODAL */}
      {activeImage && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col lg:flex-row items-center justify-center p-4 lg:p-8">
          <button
            onClick={() => setActiveImage(null)}
            className="absolute top-4 right-4 z-50 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Left: Image Canvas */}
          <div className="flex-1 w-full h-[60vh] lg:h-[85vh] flex items-center justify-center p-2">
            <img
              src={activeImage.image_url}
              alt={activeImage.title}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl border border-white/10"
            />
          </div>

          {/* Right: Info & Comments Sidebar */}
          <div className="w-full lg:w-96 h-[35vh] lg:h-[85vh] bg-[#090d16] border border-white/15 rounded-xl p-5 flex flex-col justify-between overflow-hidden shadow-2xl space-y-4">
            <div className="space-y-4 overflow-y-auto pr-1">
              <div>
                <span className="px-2 py-0.5 text-[9px] font-mono uppercase font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded">
                  {activeImage.category}
                </span>
                <h2 className="text-base font-chakra font-black uppercase text-white tracking-wide mt-2">
                  {activeImage.title}
                </h2>
                <div className="flex items-center justify-between text-[11px] font-mono text-zinc-400 mt-1">
                  <span>Autor: <strong className="text-white">{activeImage.author_username}</strong></span>
                  <span>{new Date(activeImage.created_at).toLocaleDateString("ro-RO")}</span>
                </div>
              </div>

              {activeImage.description && (
                <p className="text-xs font-mono text-zinc-300 leading-relaxed bg-black/30 p-3 rounded border border-white/5">
                  {activeImage.description}
                </p>
              )}

              {/* Tags */}
              {activeImage.tags && activeImage.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {activeImage.tags.map((t, idx) => (
                    <span key={idx} className="px-2 py-0.5 text-[10px] font-mono bg-white/5 text-zinc-400 border border-white/10 rounded">
                      #{t}
                    </span>
                  ))}
                </div>
              )}

              {/* Like Action */}
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <button
                  onClick={() => handleLike(activeImage.id)}
                  className={`px-4 py-1.5 text-xs font-chakra font-bold uppercase tracking-wider flex items-center gap-2 rounded transition-all ${
                    activeImage.likes?.includes(user?.id)
                      ? "bg-rose-500 text-white shadow-lg"
                      : "bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  <Heart className={`w-4 h-4 ${activeImage.likes?.includes(user?.id) ? "fill-white" : ""}`} />
                  <span>{activeImage.likes?.includes(user?.id) ? "Apreciat" : "Apreciază"} ({activeImage.likes?.length || 0})</span>
                </button>

                <span className="text-xs font-mono text-zinc-500">{activeImage.views || 0} vizualizări</span>
              </div>

              {/* Comments Stream */}
              <div className="space-y-3 pt-3 border-t border-white/10">
                <h4 className="text-xs font-chakra font-black uppercase text-zinc-400">
                  Comentarii ({activeImage.comments?.length || 0})
                </h4>

                <div className="space-y-2">
                  {(activeImage.comments || []).length === 0 ? (
                    <div className="text-[11px] font-mono text-zinc-500 italic py-2">Fii primul care comentează!</div>
                  ) : (
                    activeImage.comments.map((c) => (
                      <div key={c.id} className="bg-black/40 border border-white/5 p-2.5 rounded text-xs font-mono space-y-1">
                        <div className="flex items-center justify-between text-[10px]">
                          <strong className="text-[var(--tf-primary)]">{c.username}</strong>
                          <span className="text-zinc-600">{new Date(c.created_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                        <p className="text-zinc-300">{c.content}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Comment Form */}
            {user ? (
              <form onSubmit={handlePostComment} className="pt-2 border-t border-white/10 flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Scrie un comentariu..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className="flex-1 bg-black/60 border border-white/15 px-3 py-1.5 text-xs font-mono text-white rounded outline-none"
                />
                <button
                  type="submit"
                  disabled={submittingComment}
                  className="p-2 bg-[var(--tf-primary)] text-black rounded hover:brightness-110"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </form>
            ) : (
              <div className="text-[11px] font-mono text-zinc-500 text-center py-2 border-t border-white/10">
                Autentifică-te pentru a comenta.
              </div>
            )}
          </div>
        </div>
      )}

      {/* UPLOAD MODAL */}
      {uploadModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <form onSubmit={handleUpload} className="bg-[#0b0e15] border border-white/15 p-6 max-w-lg w-full space-y-4 shadow-2xl rounded-xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-sm font-chakra font-black uppercase text-white flex items-center gap-2">
                <Upload className="w-4 h-4 text-[var(--tf-primary)]" />
                <span>Încarcă Imagine în Galerie</span>
              </h3>
              <button type="button" onClick={() => setUploadModal(false)} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs font-mono max-h-[70vh] overflow-y-auto pr-1">
              <div>
                <label className="text-zinc-400 block mb-1">Titlu Imagine *</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Moment Clutch 1v4 pe Inferno"
                  value={newImage.title}
                  onChange={(e) => setNewImage({ ...newImage, title: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                />
              </div>

              <div>
                <label className="text-zinc-400 block mb-1">URL Imagine * (Direct Link)</label>
                <input
                  type="url"
                  required
                  placeholder="https://i.imgur.com/... sau link imagine"
                  value={newImage.image_url}
                  onChange={(e) => setNewImage({ ...newImage, image_url: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                />
              </div>

              <div>
                <label className="text-zinc-400 block mb-1">Categorie / Album</label>
                <select
                  value={newImage.category}
                  onChange={(e) => setNewImage({ ...newImage, category: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                >
                  <option value="screenshots">Screenshots CS 1.6 & CS2</option>
                  <option value="esports">Esports & Highlights</option>
                  <option value="setups">Setup Gaming & Battlestations</option>
                  <option value="wallpapers">Wallpapers & Art</option>
                </select>
              </div>

              <div>
                <label className="text-zinc-400 block mb-1">Taguri (separate prin virgulă)</label>
                <input
                  type="text"
                  placeholder="cs2, clutch, inferno, ace"
                  value={newImage.tags}
                  onChange={(e) => setNewImage({ ...newImage, tags: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                />
              </div>

              <div>
                <label className="text-zinc-400 block mb-1">Descriere</label>
                <textarea
                  rows="3"
                  placeholder="Detalii despre captură, serverul pe care a fost făcută..."
                  value={newImage.description}
                  onChange={(e) => setNewImage({ ...newImage, description: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <button type="button" onClick={() => setUploadModal(false)} className="px-4 py-2 bg-white/5 text-zinc-400 hover:text-white text-xs font-chakra uppercase font-bold rounded">
                Anulează
              </button>
              <button type="submit" className="px-5 py-2 bg-[var(--tf-primary)] text-black text-xs font-chakra font-black uppercase rounded shadow-lg">
                Publică în Galerie
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
